import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, HandCoins } from "lucide-react";
import { api } from "../lib/api";
import { Reveal, useSeo } from "../lib/ui";

const usd = (n) => (typeof n === "number" ? `$${n.toLocaleString("en-US")}` : "—");

const Section = ({ eyebrow, title, lead, children, testid }) => (
  <section className="mt-20 md:mt-28" data-testid={testid}>
    <Reveal>
      <p className="mono text-[0.55rem] uppercase tracking-[0.2em] text-ochre">{eyebrow}</p>
      <h2 className="mt-4 text-base font-semibold text-white md:text-lg">{title}</h2>
      {lead ? <p className="body-copy mt-4 max-w-3xl">{lead}</p> : null}
    </Reveal>
    {children}
  </section>
);

/** The modular stadium system, costed line by line, and how an opportunity reaches licensed professionals. */
export default function ModularStadiums() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useSeo({
    title: "Modular Stadiums — Costed Component By Component | KickStartKenyaFootball",
    description:
      "A modular stadium system costed component by component, from a 360-seat stand to 20,000 seats. Indicative estimates only, and no funding, transaction or payment is promised.",
  });

  useEffect(() => {
    api.get("/modular-stadiums")
      .then((r) => setData(r.data))
      .catch(() => setErr("We could not load the stadium data. Refresh to try again."));
  }, []);

  const f = data?.flagship;

  return (
    <div className="bg-black pb-24 pt-12 md:pt-16" data-testid="modular-stadiums-page">
      <div className="mx-auto max-w-[1200px] px-5 md:px-10">
        <Reveal>
          <p className="eyebrow">{data?.kicker || "Delivery"}</p>
          <h1 className="display-1 mt-5 max-w-4xl">{data?.heading || "Modular Stadiums"}</h1>
          <p className="body-copy mt-6 max-w-3xl">{data?.intro}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/apply" className="btn-primary inline-flex items-center gap-2" data-testid="stadiums-refer-cta">
              Refer a site or project <ArrowRight size={14} />
            </Link>
            <Link to="/apply" className="btn-ghost" data-testid="stadiums-apply-link">
              Tell us about an opportunity
            </Link>
            <Link to="/facility-planner" className="btn-ghost" data-testid="stadiums-planner-link">
              Facility planner
            </Link>
            <Link to="/pitch-sizing" className="btn-ghost" data-testid="stadiums-pitch-link">
              Pitch sizing and land
            </Link>
          </div>
          {data?.supplier ? (
            <p className="mono mt-8 max-w-3xl border-l-2 border-maasai bg-[#0b0b0b] p-6 text-[0.62rem] uppercase leading-relaxed tracking-[0.12em] text-zinc-300" data-testid="stadiums-disclosure">
              {data.supplier.name} — {data.supplier.relationship}{" "}
              <Link to={data.supplier.disclosure_link} className="text-ochre underline">How this is governed</Link>
            </p>
          ) : null}
        </Reveal>

        {err ? (
          <p className="mt-10 border border-maasai/40 bg-maasai/10 p-6 text-sm text-white" data-testid="stadiums-error">{err}</p>
        ) : null}

        {data?.system?.length ? (
          <Section eyebrow="The system" title="Why modular changes what a county can build" testid="stadiums-system">
            <div className="mt-8 grid gap-px bg-zinc-800 md:grid-cols-2">
              {data.system.map((s, i) => (
                <div key={s.title} className="bg-[#0b0b0b] p-6 md:p-8" data-testid={`stadiums-system-${i}`}>
                  <p className="text-sm font-semibold text-white">{s.title}</p>
                  <p className="mt-3 text-sm leading-relaxed text-zinc-400">{s.body}</p>
                </div>
              ))}
            </div>
          </Section>
        ) : null}

        {data?.ladder?.length ? (
          <Section
            eyebrow="The ladder"
            title="From one VIP unit to twenty thousand seats"
            lead="The same system at every size, so a first phase does not close off a third. Indicative estimates only, subject to survey and detailed costing."
            testid="stadiums-ladder"
          >
            <div className="mt-8 space-y-px bg-zinc-800">
              {data.ladder.map((l, i) => (
                <div key={l.tier} className="flex flex-wrap items-center justify-between gap-4 bg-[#0b0b0b] px-5 py-4 md:px-7" data-testid={`stadiums-ladder-${i}`}>
                  <p className="text-sm text-white">{l.tier}</p>
                  <p className="mono text-[0.6rem] uppercase tracking-[0.16em] text-zinc-500">
                    {l.capacity ? `${l.capacity.toLocaleString("en-US")} capacity` : "Standalone unit"}
                  </p>
                </div>
              ))}
            </div>
          </Section>
        ) : null}

        {f ? (
          <Section
            eyebrow="Worked example"
            title={f.name}
            lead="Every line of a 15,000-seat build, as quoted by the supplier. Published in full so a county, a club or a funding partner can argue with the parts rather than the headline."
            testid="stadiums-flagship"
          >
            <div className="mt-8 grid gap-px bg-zinc-800 md:grid-cols-3" data-testid="stadiums-headline">
              {[
                ["Base cost", usd(f.base_cost_usd)],
                ["Cost per seat", usd(f.cost_per_seat_usd)],
                ["With every option", usd(f.fully_loaded_usd)],
              ].map(([k, v]) => (
                <div key={k} className="bg-[#0b0b0b] p-6">
                  <p className="mono text-[0.5rem] uppercase tracking-[0.18em] text-zinc-600">{k}</p>
                  <p className="mono mt-3 text-lg text-white">{v}</p>
                </div>
              ))}
            </div>

            <div className="mt-10 grid gap-px bg-zinc-800 md:grid-cols-2">
              {f.seating.map((s) => (
                <div key={s.section} className="bg-[#0b0b0b] p-5 md:p-6">
                  <p className="mono text-[0.5rem] uppercase tracking-[0.18em] text-ochre">{s.section}</p>
                  <p className="mt-2 text-sm text-zinc-300">{s.detail}</p>
                </div>
              ))}
            </div>

            <div className="mt-12">
              <p className="mono text-[0.55rem] uppercase tracking-[0.18em] text-zinc-500">Cost stack</p>
              <div className="mt-4 space-y-px bg-zinc-800" data-testid="stadiums-line-items">
                {f.line_items.map((l, i) => (
                  <div key={l.item} className="flex flex-wrap items-baseline justify-between gap-3 bg-[#0b0b0b] px-5 py-3.5 md:px-7" data-testid={`stadiums-line-${i}`}>
                    <p className="text-sm text-zinc-300">{l.item}</p>
                    <p className="mono text-xs tracking-[0.06em] text-white">{usd(l.usd)}</p>
                  </div>
                ))}
                <div className="flex flex-wrap items-baseline justify-between gap-3 bg-[#111] px-5 py-4 md:px-7">
                  <p className="mono text-[0.6rem] uppercase tracking-[0.16em] text-ochre">Total, base build</p>
                  <p className="mono text-sm text-ochre">{usd(f.base_cost_usd)}</p>
                </div>
              </div>
            </div>

            <div className="mt-12">
              <p className="mono text-[0.55rem] uppercase tracking-[0.18em] text-zinc-500">Options</p>
              <div className="mt-4 space-y-px bg-zinc-800" data-testid="stadiums-add-ons">
                {f.add_ons.map((a, i) => (
                  <div key={a.item} className="flex flex-wrap items-baseline justify-between gap-3 bg-[#0b0b0b] px-5 py-3.5 md:px-7" data-testid={`stadiums-addon-${i}`}>
                    <div>
                      <p className="text-sm text-zinc-300">{a.item}</p>
                      {a.note ? <p className="mono mt-1 text-[0.5rem] uppercase tracking-[0.16em] text-zinc-600">{a.note}</p> : null}
                    </div>
                    <p className="mono text-xs tracking-[0.06em] text-white">{usd(a.usd)}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-12 border-l-2 border-maasai bg-[#0b0b0b] p-6 md:p-8" data-testid="stadiums-exclusions">
              <p className="mono text-[0.55rem] uppercase tracking-[0.18em] text-maasai">Not included</p>
              <p className="mt-4 text-sm leading-relaxed text-zinc-300">{f.exclusions.join(" · ")}</p>
              <p className="mono mt-5 text-[0.55rem] uppercase leading-relaxed tracking-[0.14em] text-zinc-500">
                These are the lines that decide whether a modular stadium lands on budget in Kenya. Site prep,
                foundations, services and import duty are local costs, and they are modelled per county in the
                facility planner rather than guessed here.
              </p>
            </div>
          </Section>
        ) : null}

        {data?.referral ? (
          <Section eyebrow="How it reaches the right people" title={data.referral.heading} lead={data.referral.intro} testid="stadiums-referral">
            <p className="mono border-l-2 border-maasai bg-[#0b0b0b] p-6 text-[0.6rem] uppercase leading-relaxed tracking-[0.12em] text-zinc-300" data-testid="stadiums-incentive-position">
              {data.referral.incentive_position}
            </p>

            <div className="mt-14 grid gap-px bg-zinc-800 md:grid-cols-2">
              <div className="bg-[#0b0b0b] p-6 md:p-8" data-testid="stadiums-what-to-send">
                <p className="mono text-[0.55rem] uppercase tracking-[0.18em] text-ochre">What is useful to tell us</p>
                <ul className="mt-5 space-y-2.5">
                  {data.referral.what_to_send.map((w) => (
                    <li key={w} className="flex gap-3 text-sm leading-relaxed text-zinc-300">
                      <span className="mt-2 h-1 w-1 shrink-0 bg-ochre" />
                      {w}
                    </li>
                  ))}
                </ul>
                {data.referral.not_wanted?.length ? (
                  <>
                    <p className="mono mt-8 text-[0.55rem] uppercase tracking-[0.18em] text-maasai">
                      What we do not want, and will not ask for
                    </p>
                    <ul className="mt-5 space-y-2.5" data-testid="stadiums-not-wanted">
                      {data.referral.not_wanted.map((n) => (
                        <li key={n} className="flex gap-3 text-sm leading-relaxed text-zinc-400">
                          <span className="mt-2 h-1 w-1 shrink-0 bg-maasai" />
                          {n}
                        </li>
                      ))}
                    </ul>
                  </>
                ) : null}
              </div>
              <div className="bg-[#0b0b0b] p-6 md:p-8" data-testid="stadiums-rules">
                <p className="mono text-[0.55rem] uppercase tracking-[0.18em] text-ochre">The rules, in plain terms</p>
                <ul className="mt-5 space-y-3">
                  {data.referral.rules.map((r) => (
                    <li key={r} className="border-l-2 border-zinc-800 pl-4 text-sm leading-relaxed text-zinc-400">{r}</li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mt-10 flex flex-col gap-5 border border-ochre/40 bg-[#0b0b0b] p-7 md:flex-row md:items-center md:justify-between md:p-9" data-testid="stadiums-referral-cta">
              <div className="flex items-start gap-4">
                <HandCoins size={20} className="mt-0.5 shrink-0 text-ochre" />
                <p className="max-w-2xl text-sm leading-relaxed text-zinc-300">{data.referral.route}</p>
              </div>
              <Link to={data.referral.form_link} className="btn-primary inline-flex shrink-0 items-center gap-2" data-testid="stadiums-referral-form-link">
                Tell us about an opportunity <ArrowRight size={14} />
              </Link>
            </div>

            <p className="mono mt-8 max-w-3xl text-[0.55rem] uppercase leading-relaxed tracking-[0.14em] text-zinc-500" data-testid="stadiums-terms-note">
              {data.referral.tax_position}
            </p>
          </Section>
        ) : null}

        {data?.use_cases?.length ? (
          <Section eyebrow="Where it fits" title="Four ways this lands in Kenya" testid="stadiums-use-cases">
            <div className="mt-8 grid gap-px bg-zinc-800 md:grid-cols-2">
              {data.use_cases.map((u, i) => (
                <Link key={u.title} to={u.link} className="group bg-[#0b0b0b] p-6 transition-colors hover:bg-[#111] md:p-8" data-testid={`stadiums-use-case-${i}`}>
                  <p className="text-sm font-semibold text-white">{u.title}</p>
                  <p className="mt-3 text-sm leading-relaxed text-zinc-400">{u.body}</p>
                  <span className="mono mt-5 inline-flex items-center gap-2 text-[0.55rem] uppercase tracking-[0.16em] text-ochre">
                    Open <ArrowRight size={11} className="transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              ))}
            </div>
          </Section>
        ) : null}

        {data?.source ? (
          <p className="mono mt-16 max-w-3xl text-[0.52rem] uppercase leading-relaxed tracking-[0.16em] text-zinc-600" data-testid="stadiums-source">
            Source: {data.source} · Method: {data.method} · Last reviewed {data.last_reviewed}
          </p>
        ) : null}
      </div>
    </div>
  );
}
